import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { COMPANIES, STATUSES, INVOICE_REFERENCE } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildCompanyRelationParams, resolveCompanyLogoUrl } from './companyApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('currency_id', String(values.currency_id));
  fd.append('name', values.name ?? '');
  fd.append('slug', values.slug ?? '');
  fd.append('email', values.email ?? '');
  fd.append('email_forward', values.email_forward ?? '');
  fd.append('address', values.address ?? '');
  fd.append('invoice_url', values.invoice_url ?? '');
  if (values.logoFile instanceof File) {
    fd.append('logo', values.logoFile);
  }
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);
const currencyOptLabel = (c) => c.name ?? c.code ?? String(c.id);

const CreateCompany = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const fileInputRef = useRef(null);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [refLoading, setRefLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [emailForward, setEmailForward] = useState('');
  const [address, setAddress] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [existingLogoPreview, setExistingLogoPreview] = useState('');

  const [companyRecord, setCompanyRecord] = useState(null);

  const fetchReference = useCallback(async () => {
    setRefLoading(true);
    try {
      let statusResponse;
      try {
        statusResponse = await api.get(STATUSES.BY_MODEL('company'));
      } catch {
        try {
          statusResponse = await api.get(STATUSES.BY_MODEL('Company'));
        } catch {
          statusResponse = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }
      const statusList = extractList(statusResponse?.data);
      setStatusOptions(
        (Array.isArray(statusList) ? statusList : []).map((s) => ({
          id: s.id,
          label: statusLabel(s),
          raw: s,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    }

    try {
      const curRes = await api.get(INVOICE_REFERENCE.CURRENCIES, { params: { paginate: 0 } });
      const arr = extractList(curRes.data);
      setCurrencyOptions(
        (Array.isArray(arr) ? arr : []).map((c) => ({
          id: c.id,
          label: currencyOptLabel(c),
          raw: c,
        }))
      );
    } catch {
      toast.error('Could not load currencies');
      setCurrencyOptions([]);
    } finally {
      setRefLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReference();
  }, [fetchReference]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setCompanyRecord(null);
      setExistingLogoPreview('');
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(COMPANIES.BY_ID(editId), {
          params: buildCompanyRelationParams(),
        });
        const c = data?.data ?? data;
        if (cancelled || !c) return;
        setCompanyRecord(c);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load company');
        navigate('/companies');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !companyRecord) return;
    const c = companyRecord;

    setName(c.name ?? '');
    setSlug(c.slug ?? '');
    setEmail(c.email ?? '');
    setEmailForward(c.email_forward ?? '');
    setAddress(c.address ?? '');
    setInvoiceUrl(c.invoice_url ?? '');
    setLogoFile(null);
    setExistingLogoPreview(resolveCompanyLogoUrl(c.logo));

    const st = c.status && typeof c.status === 'object' ? c.status : null;
    const sid = c.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        setStatusOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusOptions.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }

    const cur = c.currency && typeof c.currency === 'object' ? c.currency : null;
    const cid = c.currency_id ?? cur?.id;
    if (cid) {
      if (cur) {
        const opt = { id: cid, label: currencyOptLabel(cur), raw: cur };
        setSelectedCurrency(opt);
        setCurrencyOptions((prev) => (prev.some((o) => o.id === cid) ? prev : [...prev, opt]));
      } else {
        const opt = currencyOptions.find((o) => o.id === cid);
        setSelectedCurrency(opt ?? { id: cid, label: String(cid), raw: {} });
      }
    } else {
      setSelectedCurrency(null);
    }
  }, [isEdit, companyRecord, statusOptions, currencyOptions]);

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (f) setLogoFile(f);
    e.target.value = '';
  };

  const clearNewLogo = () => {
    setLogoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [logoObjectUrl, setLogoObjectUrl] = useState('');
  useEffect(() => {
    if (!logoFile) {
      setLogoObjectUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const logoPreviewSrc = logoObjectUrl || existingLogoPreview;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStatus?.id || !selectedCurrency?.id) {
      toast.warn('Select status and currency');
      return;
    }
    if (!name.trim() || !slug.trim()) {
      toast.warn('Enter name and slug');
      return;
    }
    const emailTrim = email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.warn('Enter a valid email or leave it empty');
      return;
    }
    const efTrim = emailForward.trim();
    if (efTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(efTrim)) {
      toast.warn('Enter a valid email forward or leave it empty');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      currency_id: selectedCurrency.id,
      name: name.trim(),
      slug: slug.trim(),
      email: emailTrim,
      email_forward: efTrim,
      address: address.trim(),
      invoice_url: invoiceUrl.trim(),
      logoFile,
    };

    const fd = buildFormData(values);
    const q = new URLSearchParams(buildCompanyRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${COMPANIES.BY_ID(editId)}?${q}`, fd);
        toast.success('Company updated');
      } else {
        await api.post(`${COMPANIES.LIST}?${q}`, fd);
        toast.success('Company created');
      }
      navigate('/companies');
    } catch (err) {
      toast.error(
        err.response?.data?.message || (isEdit ? 'Failed to update company' : 'Failed to create company')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} sx={{ mb: 2 }}>
          Back to companies
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit company' : 'Create company'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, v) => setSelectedStatus(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" required placeholder="Select status" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={currencyOptions}
                  value={selectedCurrency}
                  onChange={(_, v) => setSelectedCurrency(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Currency" required placeholder="Select currency" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} required fullWidth />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Logo
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Button variant="outlined" component="label" disabled={submitting}>
                    Upload logo
                    <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={onPickLogo} />
                  </Button>
                  {(logoFile || existingLogoPreview) && (
                    <>
                      <Avatar src={logoPreviewSrc || undefined} variant="rounded" sx={{ width: 64, height: 64 }} />
                      {logoFile && (
                        <Button type="button" size="small" onClick={clearNewLogo}>
                          Remove new file
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email forward"
                  type="email"
                  value={emailForward}
                  onChange={(e) => setEmailForward(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Invoice URL"
                  value={invoiceUrl}
                  onChange={(e) => setInvoiceUrl(e.target.value)}
                  fullWidth
                  placeholder="https://..."
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create company'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/companies')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateCompany;
